import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/shared/ui/Button';
import { Input } from '@/shared/ui/Input';
import { EmptyState } from '@/shared/ui/EmptyState';
import { SkeletonList } from '@/shared/ui/Skeleton';
import { ErrorState } from '@/shared/ui/ErrorState';
import { Dialog } from '@/shared/ui/Dialog';
import { Icon } from '@/shared/ui/Icon';
import { useToast } from '@/shared/ui/Toast';
import { createRoom, listRooms } from '@/shared/api/rooms';

const ROOM_ICON_TONES = ['bg-primary', 'bg-success', 'bg-warning', 'bg-primary-strong'] as const;

function initialOf(name: string): string {
  return name.trim().charAt(0) || '모';
}

function roomTone(index: number): string {
  return ROOM_ICON_TONES[index % ROOM_ICON_TONES.length];
}

export default function RoomsPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const roomsQ = useQuery({ queryKey: ['rooms'], queryFn: listRooms });
  const rooms = roomsQ.data ?? [];

  const createMut = useMutation({
    mutationFn: () =>
      createRoom({
        name: name.trim(),
        ...(description.trim() ? { description: description.trim() } : {}),
      }),
    onSuccess: (room) => {
      qc.invalidateQueries({ queryKey: ['rooms'] });
      setCreating(false);
      setName('');
      setDescription('');
      toast.success('모임방을 만들었어요.');
      navigate(`/rooms/${room.id}`);
    },
    onError: () => toast.error('모임방 생성에 실패했어요.'),
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim() || createMut.isPending) return;
    createMut.mutate();
  }

  return (
    <section className="space-y-4 pb-2" aria-label="모임방">
      {/* 페이지 제목은 상단 내비 바('모임방')가 담당 — 홈에서 들어오는 서브 화면 */}
      <header className="flex items-center justify-between gap-3 pt-1">
        <p className="min-w-0 text-[13px] font-medium leading-5 text-ink-muted">
          같은 친구들과 계속 모일 때, 채팅과 모임 기록이 한 곳에 모여요.
        </p>
        <button
          type="button"
          onClick={() => setCreating(true)}
          aria-label="새 모임방 만들기"
          className="inline-flex min-h-11 shrink-0 items-center gap-1.5 rounded-xl bg-primary-soft px-3 py-2 text-[13px] font-semibold text-primary transition-colors hover:bg-primary-light/60 focus-visible:outline-none focus-visible:shadow-focus-ring"
        >
          <Icon name="plus" size={14} />
          방 만들기
        </button>
      </header>

      {roomsQ.isLoading && <SkeletonList count={3} />}
      {roomsQ.isError && (
        <ErrorState title="모임방을 불러오지 못했어요" onRetry={() => roomsQ.refetch()} />
      )}

      {roomsQ.data && rooms.length === 0 && (
        <EmptyState
          title="아직 모임방이 없어요"
          description="같은 친구들과 여러 번 모임을 이어갈 때 만들어보세요. 채팅·모임 기록이 한 곳에 모여요."
          action={
            <Button variant="brand" onClick={() => setCreating(true)}>
              첫 모임방 만들기
            </Button>
          }
        />
      )}

      {rooms.length > 0 && (
        <section aria-label="내 모임방" className="space-y-2">
          <ul className="space-y-2" aria-label="모임방 목록">
            {rooms.map((room, index) => (
              <li key={room.id}>
                <Link
                  to={`/rooms/${room.id}`}
                  className="pressable block rounded-[22px] border border-surface-border/70 bg-surface/60 backdrop-blur-sm px-3.5 py-3 transition-colors hover:bg-surface-muted focus-visible:outline-none focus-visible:shadow-focus-ring"
                >
                  <div className="flex items-center gap-3">
                    <span
                      aria-hidden="true"
                      className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-[14px] text-[15px] font-semibold text-white ${roomTone(index)}`}
                    >
                      {initialOf(room.name)}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[15px] font-semibold tracking-[-0.3px] text-ink">
                        {room.name}
                      </span>
                      <span className="mt-1 block truncate text-[13px] font-medium text-ink-muted">
                        {room.description || `모임 ${room.meetingCount}개 진행`}
                      </span>
                    </span>
                    <span className="shrink-0 text-right">
                      <span className="block text-[13px] font-semibold text-ink">{room.memberCount}명</span>
                      <span className="mt-0.5 block text-[12px] text-ink-muted">참여</span>
                    </span>
                  </div>
                  <div className="mt-3 flex items-center justify-between border-t border-surface-border pt-2.5">
                    <span className="flex min-w-0 items-center gap-1.5 text-[10.5px] text-ink-faint">
                      <span className="flex -space-x-1.5" aria-hidden="true">
                        {[room.name, room.description ?? '모임'].slice(0, 2).map((label, avatarIndex) => (
                          <span
                            key={`${label}-${avatarIndex}`}
                            className={`flex h-[22px] w-[22px] items-center justify-center rounded-full border-2 border-white text-[8px] font-semibold text-white ${roomTone(index + avatarIndex)}`}
                          >
                            {initialOf(label)}
                          </span>
                        ))}
                      </span>
                      <span className="truncate">모임 {room.meetingCount}개 진행</span>
                    </span>
                    <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-success-soft px-2 py-1 text-[10px] font-medium text-success-strong">
                      <span className="h-1.5 w-1.5 rounded-full bg-current" />
                      활성
                    </span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      <Dialog
        open={creating}
        title="새 모임방"
        onCancel={() => {
          if (createMut.isPending) return;
          setCreating(false);
        }}
        confirmLabel={createMut.isPending ? '만드는 중…' : '만들기'}
        confirmDisabled={createMut.isPending || name.trim().length === 0}
        onConfirm={() => onSubmit({ preventDefault: () => {} } as FormEvent)}
      >
        <form onSubmit={onSubmit} className="space-y-3">
          <Input
            label="방 이름"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="예) 불금 멤버"
            maxLength={60}
            required
            aria-required="true"
            autoFocus
          />
          <Input
            label="설명 (선택)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="어떤 멤버의 방인가요?"
            maxLength={200}
          />
        </form>
      </Dialog>
    </section>
  );
}
